import { TourStopPoi } from '../models/tour-stop-poi';
import { TourService } from '../tour.service';

/**
 * Creates a navigation-aware point-of-interest tour stop.
 *
 * This utility function creates a TourStopPoi that is aware of the current URL
 * and can conditionally activate based on whether the user is on the expected page.
 * It's particularly useful for tour stops that guide users to navigate to specific
 * sections of the application.
 *
 * The created POI will only activate if the current URL does not already end with
 * the specified relative URL, preventing redundant tour stops when the user is
 * already on the target page.
 *
 * @param tourService - The tour service instance for controlling tour flow
 * @param relativeUrl - The relative URL path that this stop is associated with
 * @param selector - CSS selector for the element to highlight
 * @param displayText - The title text to display in the popover
 * @param description - The description text to display in the popover
 * @param endButtonDisplayText - The text to display on the skip/end button
 * @returns A configured TourStopPoi with navigation awareness
 *
 * @example
 * ```typescript
 * const navPoi = createTourStopNavPoi(
 *   tourService,
 *   '/dashboard/settings',
 *   '#settings-link',
 *   'Navigate to Settings',
 *   'Click here to go to the settings page where you can configure your preferences',
 *   'Skip this step'
 * );
 *
 * // This POI will only activate if the current URL doesn't end with '/dashboard/settings'
 * // If the user is already on the settings page, the POI will not activate
 * ```
 *
 * @example
 * ```typescript
 * // Using in a tour itinerary
 * const itinerary = new TourItinerary('Settings Tour', [
 *   createTourStopNavPoi(
 *     tourService,
 *     '/settings',
 *     '[data-nav="settings"]',
 *     'Go to Settings',
 *     'First, we need to navigate to the settings page',
 *     'Skip navigation'
 *   ),
 *   new TourStopPoi(
 *     '#settings-form',
 *     'Settings Form',
 *     'Here you can update your preferences',
 *     { nextButton: { displayText: 'Got it', listener: () => {} } }
 *   )
 * ]);
 * ```
 */
export function createTourStopNavPoi(
  tourService: TourService,
  relativeUrl: string,
  selector: string,
  displayText: string,
  description: string,
  nextButtonDisplayText: string,
  endButtonDisplayText: string
) {
  // Create a POI with skip functionality
  const poi = new TourStopPoi(selector, displayText, description, {
    nextButton: {
      displayText: nextButtonDisplayText,
      listener: () => {
        tourService.pauseTour();
      },
    },
    endButton: {
      displayText: endButtonDisplayText,
      listener: () => {
        tourService.skipItinerary();
      },
    },
  });

  // Override the canActivate method to check current URL
  poi.canActivate = () => {
    // Don't activate if the user is already on the target page
    if (location.href.endsWith(relativeUrl)) {
      return false;
    }

    return true;
  };

  return poi;
}
